import { MOBILE_SSO_CODE_VERIFIER_KEY } from "./startSsoRedirect";

/**
 * Checks if the callback URL contains an SSO_ACCOUNT_LINKED error from the
 * PreSignUp Lambda. This happens on an employee's first SSO login when they
 * already have a Cognito account (via magic link/password). The Lambda links
 * the Rippling identity to the existing user and throws to prevent Cognito
 * from creating a duplicate — this is the AWS-documented pattern.
 */
export function isAccountLinkedError(): boolean {
  const params = new URLSearchParams(globalThis.location.search);
  const errorDescription = params.get("error_description");
  return errorDescription?.includes("SSO_ACCOUNT_LINKED") === true;
}

/**
 * Detects a flow where Amplify has no stored OAuth state (because
 * signInWithRedirect was never called from this app). This happens when
 * the user initiates SSO from outside the app (e.g. the Rippling tile).
 * Amplify can't exchange the auth code without the code verifier, so the
 * frontend must re-initiate the flow.
 */
export function isIdpInitiatedFlow(): boolean {
  const params = new URLSearchParams(globalThis.location.search);
  if (!params.has("code")) {
    return false;
  }

  // On Capacitor, the mobile SSO flow stores its own PKCE verifier instead
  // of using Amplify's oauthState. If our key exists, the flow was app-initiated.
  if (localStorage.getItem(MOBILE_SSO_CODE_VERIFIER_KEY)) {
    return false;
  }

  // Amplify v6 (6.16.3) stores OAuth state in localStorage with keys
  // containing "oauthState". If no such key exists, Amplify did not
  // initiate this flow. This relies on an Amplify implementation detail
  // — verify after upgrades.
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.includes("oauthState")) {
      return false;
    }
  }
  return true;
}

/** Keys to clear from localStorage before hydrating a new SSO session. */
const STALE_SESSION_KEYS = ["selectedUser", "selectedFacility", "AUTH_TOKEN", "state"] as const;

/**
 * Clears stale user-scoped data from the previous session before hydrating
 * the new SSO session. Intentionally does NOT clear CognitoIdentityServiceProvider
 * keys — Amplify just wrote the fresh tokens from the token exchange and
 * initCognitoSession needs them.
 */
export function clearStaleSessionKeys(): void {
  for (const key of STALE_SESSION_KEYS) {
    localStorage.removeItem(key);
  }
}
